import { observer } from "mobx-react-lite";
import * as React from "react";
import { classNames } from "src/common/classNames";
import { Empty } from "antd";
import { onError } from "src/common/onError";
import styles from "./Sample2Table.module.less";
import { Sample2Column } from "./Sample2Column";
import { ZSample2Row } from "../../ZSample2";

type PropsSample2Table = {
  columns: Sample2Column[];
  rows: ZSample2Row[];
};

export const Sample2Table: React.FC<PropsSample2Table> = observer((props) => {
  const { columns, rows } = props;
  const gridTemplateColumns = makeGridTemplates(columns);

  const refHeader = React.useRef<HTMLDivElement>(null);
  const refBody = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    const body = refBody.current;
    const resizeObserver = new ResizeObserver((entries) => {
      window.requestAnimationFrame((): undefined | void => {
        if (!Array.isArray(entries) || !entries.length) {
          return;
        }
        try {
          entries.forEach((entry) => {
            if (entry.target === body) {
              if (refHeader.current) {
                refHeader.current.style.width = `${entry.contentRect.width}px`;
              }
            }
          });
        } catch (e) {
          onError(e);
        }
      });
    });
    if (body) {
      resizeObserver.observe(body);
    }
    const handleError = (e: ErrorEvent) => {
      onError(e.error);
    };
    window.addEventListener("error", handleError);
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("error", handleError);
    };
  }, [refBody.current]);

  return (
    <div className={styles.tableBox}>
      {/* Header */}
      <div
        className={styles.header}
        ref={refHeader}
        style={{ gridTemplateColumns }}
      >
        {columns.map(({ key, title, titleAlign }, i) => (
          <div
            key={key}
            className={classNames([
              styles.th,
              styles.borderTop,
              styles.borderRB,
              [i === 0, styles.borderLeft],
              clsAlign(titleAlign),
            ])}
          >
            {title}
          </div>
        ))}
      </div>

      <div
        ref={refBody}
        className={styles.body}
        onScroll={(e) => {
          if (refHeader.current) {
            refHeader.current.scrollLeft = e.currentTarget.scrollLeft;
          }
        }}
      >
        <div className={styles.cells} style={{ gridTemplateColumns }}>
          {rows.length === 0 && (
            <div
              className={classNames([
                styles.borderLeft,
                styles.borderRB,
                styles.emptyBox,
              ])}
              style={{ gridColumn: `span ${columns.length}` }}
            >
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
            </div>
          )}
          {rows.map((row, rowIndex) => (
            <React.Fragment key={row.id}>
              {columns.map(({ key, render, align }, j) => (
                <div
                  key={key}
                  className={classNames([
                    styles.borderRB,
                    [j === 0, styles.borderLeft],
                    clsAlign(align),
                  ])}
                >
                  {render(row, rowIndex)}
                </div>
              ))}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
});

const makeGridTemplates = (columns: Sample2Column[]): string =>
  columns
    .map(({ width, minWidth }) => {
      if (!width) {
        return `${minWidth}px`;
      }
      return `minmax(${minWidth}px,${width})`;
    })
    .join(" ");

const clsAlign = (align: "center" | "left" | "right" | undefined) =>
  align ? styles[`${align}Align`] : undefined;
